// Verbindet ein Y.Doc per y-websocket mit dem Backend-Realtime-Server.
// Token wird via params.token an die URL angehaengt (siehe backend/realtime/server.ts).
// Provider/Doc werden beim Unmount sauber zerlegt.

import { useEffect, useState } from "react";
import { WebsocketProvider } from "y-websocket";
import * as Y from "yjs";
import { readStoredAuth } from "../auth/storage.js";

export type RealtimeStatus = "connecting" | "connected" | "disconnected";

export interface BoardDocHandle {
  doc: Y.Doc;
  provider: WebsocketProvider;
  status: RealtimeStatus;
}

/**
 * Realtime-URL fuer den y-websocket-Provider.
 *
 * - Explizites `VITE_REALTIME_URL` (Build-Zeit) hat Vorrang — fuer Sonderfaelle / Tests.
 * - Sonst leitet sich der Endpoint aus `window.location` ab:
 *     `https:` → `wss://<host>/ws`, sonst `ws://<host>/ws`.
 *   So spricht prod (TLS via Reverse-Proxy) automatisch `wss://`, ohne dass das
 *   Image neu gebaut werden muss (Optional O1).
 */
export function getRealtimeUrl(): string {
  const env = import.meta.env.VITE_REALTIME_URL;
  if (typeof env === "string" && env.length > 0) return env;
  if (typeof window !== "undefined" && window.location) {
    const scheme = window.location.protocol === "https:" ? "wss:" : "ws:";
    return `${scheme}//${window.location.host}/ws`;
  }
  return "ws://localhost:5173/ws";
}

export function useBoardDoc(boardId: string | null): BoardDocHandle | null {
  const [handle, setHandle] = useState<BoardDocHandle | null>(null);

  useEffect(() => {
    if (!boardId) {
      setHandle(null);
      return;
    }
    const auth = readStoredAuth();
    if (!auth) {
      setHandle(null);
      return;
    }

    const doc = new Y.Doc();
    const provider = new WebsocketProvider(getRealtimeUrl(), `board/${boardId}`, doc, {
      params: { token: auth.token },
      // Awareness anschalten — Server broadcastet, wir senden auch.
      connect: true,
    });

    setHandle({ doc, provider, status: "connecting" });

    const onStatus = (event: { status: string }): void => {
      const next: RealtimeStatus =
        event.status === "connected"
          ? "connected"
          : event.status === "disconnected"
            ? "disconnected"
            : "connecting";
      setHandle((prev) => (prev ? { ...prev, status: next } : prev));
    };
    provider.on("status", onStatus);

    return () => {
      provider.off("status", onStatus);
      provider.destroy();
      doc.destroy();
      setHandle(null);
    };
  }, [boardId]);

  return handle;
}
